import { onClickOutside } from '../utils/on-click-outside';
import { enableDragScroll } from '../utils/drag-scroll';
import { openZoom, zoomLightbox } from '../components/product-zoom';
import { openBackInStock } from '../components/back-in-stock';
import { onMouse } from '../utils/has-mouse';

// VIEW MORE: mouse users can click and drag the row sideways
enableDragScroll('.m-products--large .m-products__list');

const slider = document.querySelector('.m-item__images') as HTMLElement;
const sliderImages = document.querySelector('.m-item__image-list') as HTMLElement;
let sliderInstance: any | null;

const itemInfo = document.querySelector('.m-item__info') as HTMLElement;
const guideToggle = document.querySelector('.m-item__guide-toggle');

// show/hide more info
document.querySelector('.c-action__more')?.addEventListener('click', () => {
  itemInfo?.classList.add('is-expanded');

  onClickOutside(itemInfo, () => itemInfo?.classList.remove('is-expanded'));
});

document.querySelector('.m-item__show-less')?.addEventListener('click', () => {
  itemInfo?.classList.remove('is-expanded');
});

// Computer: CLOSE stays put at the top right as you scroll, even once the details panel has
// scrolled away. It fades out only when the bottom line of the photos and ADD TO CART comes up
// to it, and comes back when you scroll up again.
const showLess = document.querySelector<HTMLElement>('.m-item__show-less');
const placeShowLess = () => {
  if (!showLess || !itemInfo || !sliderImages) return;
  const photosEnd = sliderImages.getBoundingClientRect().bottom;
  // where CLOSE is pinned (its own rectangle moves while it fades in, so not that)
  const closeBottom = parseFloat(getComputedStyle(showLess).top) + showLess.offsetHeight;
  const past = photosEnd <= closeBottom + 2;
  if (!past && itemInfo.classList.contains('is-past-photos')) {
    // coming back: show it at once, without the fade it has when the description opens
    itemInfo.classList.add('is-back-from-photos');
    window.setTimeout(() => itemInfo.classList.remove('is-back-from-photos'), 50);
  }
  itemInfo.classList.toggle('is-past-photos', past);
};
window.addEventListener('scroll', placeShowLess, { passive: true });
window.addEventListener('resize', placeShowLess);
placeShowLess();

// toggle size guide
guideToggle?.addEventListener('click', () => {
  const guideContent = document.querySelector('.m-item__guide-content') as HTMLElement;
  guideContent.classList.toggle('is-expanded');
  guideToggle.classList.toggle('is-expanded');
});

// Image slider for mobile
const initializeSlider = (): void => {
  var sliderEl = sliderImages;
  sliderInstance = new (window as any).Flickity( sliderEl, {
    cellAlign: 'left',
    contain: true,
    prevNextButtons: false,
    pageDots: false,
    imagesLoaded: true,
    autoPlay: true
  });
  
  const buttons = document.querySelector('.slider__arrows') as HTMLElement;
  const prevButton = buttons.querySelector('.slider__arrow--left') as HTMLElement;
  const nextButton = buttons.querySelector('.slider__arrow--right') as HTMLElement;

  prevButton.addEventListener('click', () => {
    sliderInstance.previous();
    sliderInstance.pausePlayer();
  });
  nextButton.addEventListener('click', () => {
    sliderInstance.next();
    sliderInstance.pausePlayer();
  });

  // Only → on the first photo, only ← on the last, both in between
  const flkty = sliderInstance;
  const updateArrows = (): void => {
    const last = flkty.slides.length - 1;
    prevButton.classList.toggle('is-hidden', flkty.selectedIndex <= 0);
    nextButton.classList.toggle('is-hidden', flkty.selectedIndex >= last);
  };
  flkty.on('select', updateArrows);
  updateArrows();

  // tap (not swipe) on a photo opens the zoom
  flkty.on('staticClick', (_event: Event, _pointer: unknown, _cell: HTMLElement, cellIndex?: number) => {
    if (typeof cellIndex !== 'number') return;
    flkty.pausePlayer();
    openZoom(cellIndex);
  });
};

const destroySlider = (): void => {
  if (sliderInstance) {
    sliderInstance.destroy();
    sliderInstance = null;
  }
};

const debounce = (func: (...args: any[]) => void, wait: number): (...args: any[]) => void => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: any[]) => {
    clearTimeout(timeout);

    // @ts-ignore next line
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

const handleResize = debounce((): void => {
  if (window.innerWidth <= 720) {
    if (!sliderInstance) {
      // addSliderClasses();
      initializeSlider();
    }
  } else {
    // removeSliderClasses();
    destroySlider();
  }
}, 100);

if (slider) {
  document.addEventListener('DOMContentLoaded', () => {
    handleResize();
    window.addEventListener('resize', handleResize);
  
    return () => {
      window.removeEventListener('resize', handleResize);
      destroySlider();
    };
  });
}

// Product videos: load and play only while on screen (also inside the mobile slider)
const productVideos = Array.from(document.querySelectorAll('.m-item__video video')) as HTMLVideoElement[];

if (productVideos.length && 'IntersectionObserver' in window) {
  const videoObserver = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      const video = target as HTMLVideoElement;
      if (isIntersecting) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, { threshold: 0.25 });

  productVideos.forEach((video) => videoObserver.observe(video));
}

// Product photo / video zoom (scripts/components/product-zoom.ts)
// Desktop: click a photo. Mobile: handled by the slider's staticClick above.
document.querySelectorAll('.m-item__image[data-zoom-src], .m-item__image[data-zoom-video]').forEach((photo, index) => {
  photo.addEventListener('click', () => {
    if (sliderInstance) return;
    openZoom(index);
  });
});

// after closing, leave the mobile slider on the last photo viewed
zoomLightbox?.on('close', () => {
  const index = zoomLightbox.pswp?.currIndex;
  if (sliderInstance && typeof index === 'number') sliderInstance.select(index, false, true);
});

// FORM LOGIC
const allSizesSelectors = document.querySelectorAll('.c-shop-sizes') as NodeListOf<HTMLElement>;
const mobileContentSizes = document.querySelector('.header-mob__sizes') as HTMLElement;
const mobileContentSizesPlaceholder = document.querySelector('.header-mob__sizes-placeholder') as HTMLElement;
const mobileContentSizesList = document.querySelector('.header-mob__sizes-list') as HTMLElement;
const mobileContentSizesButton = document.querySelector('.header-mob__sizes-btn') as HTMLButtonElement;
const addButton = document.querySelector('.c-action__add') as HTMLButtonElement;
const addButtonMobile = document.querySelector('.header-mob__add') as HTMLButtonElement;
const notifyButtons = Array.from(
  document.querySelectorAll('.c-notify')
) as HTMLButtonElement[];
const allQuantitySelectors = document.querySelectorAll('.c-input-number') as NodeListOf<HTMLElement>;

// Keep the SIZE arrow (↓ / ↑) in sync with the open/closed state
function syncMobileSizesButton() {
  if (!mobileContentSizes || !mobileContentSizesButton) return;

  const isExpanded = mobileContentSizes.classList.contains('is-expanded');
  mobileContentSizesButton.textContent = isExpanded ? 'SIZE ↑' : 'SIZE ↓';
  mobileContentSizesButton.setAttribute('aria-expanded', String(isExpanded));
}

// Tap SIZE to open and close the mobile size list
mobileContentSizesButton?.addEventListener('click', () => {
  mobileContentSizes?.classList.toggle('is-expanded');
  syncMobileSizesButton();
});

syncMobileSizesButton();

// Keep different product sizes radio in sync
function syncSizes(value: string, label?: string) {
  allSizesSelectors.forEach(sizeSelector => {
    const sizeInputs = sizeSelector.querySelectorAll('input[type="radio"]') as NodeListOf<HTMLInputElement>;
  
    sizeInputs.forEach(input => {
      if (input.value === value) {
        input.checked = true;
      }
    });
  });

  if (mobileContentSizes && mobileContentSizesList) {
    // the list stays open, so you can try one size after another and watch the button
    // change between ADD TO CART and NOTIFY ME; SIZE ↑ closes it
    syncMobileSizesButton();
    mobileContentSizesPlaceholder.textContent = label || '';
  }

  showAddOrNotify(value, label);
}

/*
 * A sold-out size swaps ADD TO CART for NOTIFY ME (components/back-in-stock.ts):
 * on a mouse as soon as the size is hovered, and for everyone once it is chosen.
 */
const showAddOrNotify = (value: string, label?: string) => {
  const size = document.querySelector(
    `.c-shop-sizes__radio[value="${value}"]`
  ) as HTMLInputElement | null;
  const soldOut = size?.dataset.available === 'false';

  if (addButton) {
    addButton.disabled = soldOut;
    addButton.hidden = soldOut;
  }
  if (addButtonMobile) {
    addButtonMobile.disabled = soldOut;
    addButtonMobile.hidden = soldOut;
  }
  notifyButtons.forEach((button) => {
    button.hidden = !soldOut;
    button.dataset.variant = value;
    button.dataset.size = label || size?.dataset.label || '';
  });
};

// back to whichever size is actually chosen (or to plain ADD TO CART if none is)
const restoreAddOrNotify = () => {
  const chosen = document.querySelector(
    '.c-shop-sizes__radio:checked'
  ) as HTMLInputElement | null;

  if (chosen) {
    showAddOrNotify(chosen.value, chosen.dataset.label);
    return;
  }

  if (addButton) addButton.hidden = false;
  if (addButtonMobile) addButtonMobile.hidden = false;
  notifyButtons.forEach((button) => (button.hidden = true));
};

// mouse only: a phone has no hover, and iOS fires hover-shaped events on a tap
onMouse(() => {
  // leaving the size waits a moment, so the mouse can travel to NOTIFY ME and click it
  let leaving: number | undefined;

  const keep = () => window.clearTimeout(leaving);
  const leave = () => {
    keep();
    leaving = window.setTimeout(restoreAddOrNotify, 300);
  };

  document.querySelectorAll('.c-shop-sizes__radio').forEach((el) => {
    const size = el as HTMLInputElement;
    if (size.dataset.available !== 'false') return;

    const label = size.closest('label') || size;
    label.addEventListener('mouseenter', () => {
      // a chosen size wins: otherwise, on the way to the button, the sizes the
      // mouse passes over would each take the button over in turn
      if (document.querySelector('.c-shop-sizes__radio:checked')) return;

      keep();
      showAddOrNotify(size.value, size.dataset.label);
    });
    label.addEventListener('mouseleave', leave);
  });

  notifyButtons.forEach((button) => {
    button.addEventListener('mouseenter', keep);
    button.addEventListener('mouseleave', leave);
  });
});

// the product's name for the sign-up panel: model and colour, as the page shows them
const productName = [
  document.querySelector('.header-mob__cat')?.textContent,
  document.querySelector('.header-mob h1')?.textContent,
]
  .map((part) => part?.trim())
  .filter(Boolean)
  .join(' ');

notifyButtons.forEach((button) => {
  button.addEventListener('click', (event) => {
    // the panel closes on a click outside it, and this button is outside (see
    // modules/footer.ts): without this it would shut the panel it just opened
    event.stopPropagation();

    const variant = button.dataset.variant || '';
    const size = button.dataset.size || '';

    // tick the size the panel is about to name, so the page agrees with it
    if (variant) syncSizes(variant, size);

    openBackInStock(variant, size, productName || '');
  });
});

// Keep different quantity inputs in sync
function syncQuantities(value: string) {
  allQuantitySelectors.forEach(quantitySelector => {
    const quantityInput = quantitySelector.querySelector('.c-input-number__input') as HTMLInputElement;
    const inputValue = quantitySelector?.querySelector('.c-input-number__value') as HTMLDivElement;
    quantityInput.value = value;
    inputValue.textContent = value
  });
}

allSizesSelectors?.forEach(sizeSelector => {
  const sizeInputs = sizeSelector.querySelectorAll('input[type="radio"]') as NodeListOf<HTMLInputElement>;

  sizeInputs.forEach(input => {
    input.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement;
      syncSizes(target.value, target.dataset.label);
    });
  });
});

allQuantitySelectors?.forEach(quantitySelector => {
  const input = quantitySelector.querySelector('.c-input-number__input') as HTMLInputElement;

  input.addEventListener('change', (event) => {
    const target = event.target as HTMLInputElement;
    syncQuantities(target.value);
  });
});

// mobileContentSizes?.addEventListener('change', (event) => {
//   const target = event.target as HTMLSelectElement;
//   syncSizes(target.value);
// });